// Erweiterte Profilseite mit Live-Speicherung und Animation inkl. Dropdowns & Bild-Upload
import { useEffect, useState, useRef, ChangeEvent } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useUserProfile } from "../contexts/UserProfileContext";
import { useCreditBalance } from "../hooks/useCreditBalance";
import CreditPurchaseModal from "../components/CreditPurchaseModal";
import clsx from "clsx";
import { Eye, Users, ShieldCheck, Lock } from "lucide-react";
import MultiSelectInput from "../components/form/MultiSelectInput";
import PushPermissionSettings from "../components/PushPermissionSettings";
import PushSettingsSection from "../components/PushSettingsSection";
import EmailSettingsSection from "../components/EmailSettingsSection";

const topLanguages = [
  "Albanisch",
  "Arabisch",
  "Aragonesisch",
  "Aromanisch",
  "Armenisch",
  "Aserbaidschanisch",
  "Asturisch",
  "Baschkirisch",
  "Baskisch",
  "Belarussisch",
  "Bengalisch",
  "Bosnisch",
  "Bretonisch",
  "Bulgarisch",
  "Dänisch",
  "Deutsch",
  "Englisch",
  "Estnisch",
  "Färöisch",
  "Finnisch",
  "Flämisch",
  "Französisch",
  "Friesisch",
  "Galicisch",
  "Georgisch",
  "Gotisch",
  "Griechisch",
  "Gujarati",
  "Hausa",
  "Hindi",
  "Indonesisch",
  "Irisch",
  "Isländisch",
  "Italienisch",
  "Japanisch",
  "Javanisch",
  "Kasachisch",
  "Katalanisch",
  "Koreanisch",
  "Korsisch",
  "Kroatisch",
  "Kurdisch",
  "Lettisch",
  "Litauisch",
  "Luxemburgisch",
  "Malaiisch",
  "Maltesisch",
  "Mandarin",
  "Marathi",
  "Mazedonisch",
  "Moldawisch",
  "Montenegrinisch",
  "Niederländisch",
  "Norwegisch",
  "Okzitanisch",
  "Persisch",
  "Polnisch",
  "Portugiesisch",
  "Punjabi",
  "Rätoromanisch",
  "Rumänisch",
  "Russisch",
  "Sami",
  "Sardisch",
  "Schottisch-Gälisch",
  "Schwedisch",
  "Serbisch",
  "Sizilianisch",
  "Slowakisch",
  "Slowenisch",
  "Sorbisch",
  "Spanisch",
  "Swahili",
  "Tamil",
  "Telugu",
  "Thai",
  "Tschechisch",
  "Türkisch",
  "Ukrainisch",
  "Ungarisch",
  "Urdu",
  "Vietnamesisch",
  "Walisisch",
  "Zazaisch",
];

export const LICENSE_OPTIONS = [
  "Auto",
  "Motorrad",
  "LKW",
  "Bus",
  "Traktor",
  "Anhänger",
  "Motorboot",
  "Segelboot",
  "Küstenschein",
  "Hochseeschein",
  "Kleinflugzeug",
  "Drohne",
];

export const NOTIFICATION_OPTIONS = [
  "E-Mail",
  "SMS",
  "Whatsapp",
  "Signal",
  "Keine Benachrichtigungen",
];

const VisibilityLegend = () => (
  <div className="mb-6 sticky top-20 z-10 pt-2 pb-2 bg-[#161b27]">
    <div className="bg-[#1e2639] border border-[#2a3347] rounded-xl px-4 py-3">
      <p className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-2">Legende: Sichtbarkeit</p>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
        <span className="flex items-center gap-1.5 text-xs text-slate-300">
          <Lock size={13} className="text-slate-100" />
          Nur ich
        </span>
        <span className="flex items-center gap-1.5 text-xs text-slate-400">
          <ShieldCheck size={13} className="text-slate-500" />
          Veranstalter
        </span>
        <span className="flex items-center gap-1.5 text-xs text-slate-400">
          <Users size={13} className="text-slate-500" />
          Alle
        </span>
        <span className="flex items-center gap-1.5 text-xs text-slate-400">
          <Eye size={13} className="text-slate-500" />
          Privat-Organisator
        </span>
      </div>
    </div>
  </div>
);

const resizeImage = (
  file: File,
  maxWidth = 300,
  quality = 0.6
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      if (!e.target?.result) return;
      img.src = e.target.result as string;
    };

    img.onload = () => {
      const scale = maxWidth / img.width;
      const canvas = document.createElement("canvas");
      canvas.width = maxWidth;
      canvas.height = img.height * scale;

      const ctx = canvas.getContext("2d");
      if (!ctx) return reject("Kein Canvas-Kontext verfügbar");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject("Fehler beim Komprimieren");
        },
        "image/jpeg",
        quality
      );
    };

    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
};

type PictureUploaderProps = {
  url: string | null;
  onUpload: (url: string) => void;
  currentPath?: string | null;
};

const ProfilePictureUploader = ({
  url,
  onUpload,
  currentPath,
}: PictureUploaderProps) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);


  const handleUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      if (!e.target.files || e.target.files.length === 0)
        throw new Error("Keine Datei ausgewählt");

      const file = e.target.files[0];
      const fileExt = file.name.split(".").pop();
      const resizedBlob = await resizeImage(file);
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      const filePath = `${fileName}`;

      // 🧹 Schritt 1: Altes Bild löschen (wenn vorhanden)
      if (currentPath) {
        const { error: removeError } = await supabase.storage
          .from("profile-pictures")
          .remove([currentPath]);

        if (removeError) {
          console.error("❌ Fehler beim Löschen:", removeError.message);
        } else {
        }
      }

      // ⬆️ Schritt 2: Neues Bild hochladen
      const { error: uploadError } = await supabase.storage
        .from("profile-pictures")
        .upload(filePath, resizedBlob, {
          contentType: "image/jpeg",
          upsert: true,
        });

      if (uploadError) throw uploadError;

      onUpload(filePath); // ⬅️ wichtig: neuer Pfad zurückmelden
    } catch (error) {
      alert("Fehler beim Hochladen des Profilbilds");
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mb-6">
      <label className="block mb-2 text-sm text-slate-400 font-medium">Profilbild</label>
      <div
        className="w-32 h-32 rounded-full overflow-hidden border border-[#2a3347] cursor-pointer relative"
        onClick={() => fileInputRef.current?.click()}
        title="Klicken zum Ändern"
      >
        {url ? (
          <img
            src={url}
            alt="Profilbild"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-[#1e2639] flex items-center justify-center text-slate-500">
            Foto hinzufügen
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-[#0d1117]/60 flex items-center justify-center text-sm text-slate-300">
            Hochladen...
          </div>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleUpload}
        disabled={uploading}
        className="hidden"
      />
    </div>
  );
};

const visibilityOptions = [
  { label: "Nur ich", value: "self" },
  { label: "Nur Veranstalter", value: "organizer" },
  { label: "Alle", value: "all" },
  { label: "Nur Veranstalter privater Trips", value: "private_organizer" },
];

const visibilityIconMap = [
  { value: "self", icon: <Lock size={24} />, label: "Nur ich" },
  {
    value: "organizer",
    icon: <ShieldCheck size={24} />,
    label: "Veranstalter",
  },
  { value: "all", icon: <Users size={24} />, label: "Alle" },
  {
    value: "private_organizer",
    icon: <Eye size={24} />,
    label: "Privat-Organisator",
  },
];

type VisibilityIconsProps = {
  value: string;
  onChange: (value: string) => void;
  isSaving: boolean;
};

const VisibilityIcons = ({
  value,
  onChange,
  isSaving,
}: VisibilityIconsProps) => (
  <div className="flex gap-2 mt-1">
    {visibilityIconMap.map((opt) => (
      <button
        key={opt.value}
        onClick={() => onChange(opt.value)}
        className={clsx(
          "p-1 rounded hover:bg-[#1e2639] transition",
          value === opt.value ? "text-slate-100" : "text-slate-500",
          isSaving && "animate-pulse"
        )}
        title={opt.label}
        type="button"
      >
        {opt.icon}
      </button>
    ))}
  </div>
);

type FieldProps = {
  label: string;
  type?: string;
  value: string | number | undefined;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isSaving: boolean;
  rightElement?: React.ReactNode;
};

type SelectProps = {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: string[];
  isSaving: boolean;
  rightElement?: React.ReactNode;
};

type CheckboxProps = {
  label: string;
  value: boolean | undefined;
  onChange: (val: boolean) => void;
  isSaving: boolean;
  rightElement?: React.ReactNode;
};

const dietOptions = ["Omnivor", "Vegetarisch", "Vegan", "Pescetarisch"];
const genderOptions = ["männlich", "weiblich", "divers"];
const sizeOptions = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];

const ProfileField = ({
  label,
  type = "text",
  value,
  onChange,
  isSaving,
  rightElement,
}: FieldProps) => (
  <div className="mb-4">
    <div className="flex justify-between items-center mb-1">
      <label className="text-sm text-slate-400 font-medium">{label}</label>
      {rightElement && <div className="flex gap-1 bg-[#1e2639] border border-[#2a3347]/60 rounded-lg px-1.5 py-0.5">{rightElement}</div>}
    </div>
    <input
      type={type}
      className={clsx(
        "w-full p-2 border rounded transition bg-[#0d1117] text-slate-100",
        isSaving ? "border-sky-400 animate-pulse" : "border-[#2a3347]"
      )}
      value={value ?? ""}
      onChange={onChange}
    />
  </div>
);

const SelectField = ({
  label,
  value,
  onChange,
  options,
  isSaving,
  rightElement,
}: SelectProps) => (
  <div className="mb-4">
    <div className="flex justify-between items-center mb-1">
      <label className="text-sm text-slate-400 font-medium">{label}</label>
      {rightElement && <div className="flex gap-1 bg-[#1e2639] border border-[#2a3347]/60 rounded-lg px-1.5 py-0.5">{rightElement}</div>}
    </div>
    <select
      className={clsx(
        "w-full p-2 border rounded transition bg-[#0d1117] text-slate-100",
        isSaving ? "border-sky-400 animate-pulse" : "border-[#2a3347]"
      )}
      value={value ?? ""}
      onChange={onChange}
    >
      <option value="">Bitte wählen</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  </div>
);

const CheckboxField = ({
  label,
  value,
  onChange,
  isSaving,
  rightElement,
}: CheckboxProps) => (
  <div className="mb-4">
    <div className="flex justify-between items-center mb-1">
      <label className="text-sm text-slate-400 font-medium">{label}</label>
      {rightElement && <div className="flex gap-1 bg-[#1e2639] border border-[#2a3347]/60 rounded-lg px-1.5 py-0.5">{rightElement}</div>}
    </div>
    <div className="flex items-center gap-2">
      <input
        type="checkbox"
        checked={value || false}
        onChange={(e) => onChange(e.target.checked)}
        className={clsx("w-5 h-5", isSaving && "animate-pulse")}
      />
    </div>
  </div>
);

export default function Profile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshProfile } = useUserProfile();
  const { balance: creditBalance, reload: reloadCredits } = useCreditBalance();
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [paymentSuccessMsg, setPaymentSuccessMsg] = useState("");

  const [profile, setProfile] = useState<any>({});
  const [savingField, setSavingField] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [signedProfilePicUrl, setSignedProfilePicUrl] = useState<string | null>(
    null
  );

  // Payment-Rückkehr von Stripe verarbeiten
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const payment = params.get("payment");
    const credits = params.get("credits");
    if (payment === "success" && credits) {
      setPaymentSuccessMsg(`✓ ${credits} Svevana Credits wurden deinem Konto gutgeschrieben.`);
      reloadCredits();
      // URL-Parameter bereinigen
      navigate("/profile", { replace: true });
      setTimeout(() => setPaymentSuccessMsg(""), 6000);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();
      if (error) {
        console.error(error);
        setError("Fehler beim Laden des Profils");
      } else {
        setProfile(data);
        if (data?.profile_picture_url) {
          const { data: signed, error: urlError } = await supabase.storage
            .from("profile-pictures")
            .createSignedUrl(data.profile_picture_url, 60 * 60); // ✅ KORREKTES FELD
          if (!urlError) setSignedProfilePicUrl(signed?.signedUrl ?? null);
        }
      }
      setLoading(false);
    };
    fetchProfile();
  }, [user]);

  const handleFieldChange = async (
    field: string,
    value: any,
    callback?: () => void
  ) => {
    setProfile((prev: any) => ({ ...prev, [field]: value }));
    setSavingField(field);

    const { error } = await supabase
      .from("profiles")
      .update({ [field]: value })
      .eq("id", user!.id);

    if (!error) {
      await refreshProfile();
      if (callback) callback();
    }

    setTimeout(() => setSavingField(null), 800);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const handlePasswordChange = async () => {
    setSuccess("");
    setError("");
    if (!oldPassword || !newPassword) {
      setError("Bitte altes und neues Passwort eingeben");
      return;
    }
    const { error: loginError } = await supabase.auth.signInWithPassword({
      email: user?.email || "",
      password: oldPassword,
    });
    if (loginError) {
      setError("Altes Passwort ist falsch");
      return;
    }
    const { error: pwError } = await supabase.auth.updateUser({
      password: newPassword,
    });
    if (pwError) {
      setError("Fehler beim Ändern des Passworts");
    } else {
      setSuccess("Passwort erfolgreich geändert ✅");
      setOldPassword("");
      setNewPassword("");
    }
  };

  if (loading)
    return <div className="min-h-screen bg-[#0d1117] flex items-center justify-center text-slate-500">Profil wird geladen...</div>;

  return (
    <>
    <div className="min-h-screen bg-[#0d1117]">
    <div className="max-w-2xl mx-auto pt-20 pb-20 px-4">

      {/* Payment-Erfolgsmeldung */}
      {paymentSuccessMsg && (
        <div className="mb-4 bg-emerald-900/30 border border-emerald-700 rounded-xl px-4 py-3 text-sm text-emerald-400">
          {paymentSuccessMsg}
        </div>
      )}

    <div className="bg-[#161b27] rounded-2xl border border-[#2a3347] p-6">
      <h1 className="text-2xl font-bold text-slate-100 mb-4">Mein Profil</h1>

      {/* Svevana Credits */}
      <div className="bg-[#1e2639] border border-[#2a3347] rounded-xl px-4 py-4 mb-6">
        <p className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-2">Svevana Credits</p>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-2xl font-bold text-slate-100">
              {creditBalance !== null ? `${creditBalance} SC` : <span className="animate-pulse text-slate-500 text-base">Wird geladen…</span>}
            </p>
            {creditBalance !== null && (
              <p className="text-xs text-slate-500 mt-0.5">≈ {Math.floor(creditBalance / 40)} KI-Planungen</p>
            )}
          </div>
          <button
            onClick={() => setShowPurchaseModal(true)}
            className="bg-sky-500 text-white px-4 py-2 rounded-lg hover:bg-sky-600 transition text-sm font-medium"
          >
            Aufladen
          </button>
        </div>
      </div>

      <ProfilePictureUploader
        url={signedProfilePicUrl}
        currentPath={profile.profile_picture_url ?? null}
        onUpload={(filePath) => {
          handleFieldChange("profile_picture_url", filePath, async () => {
            const { data: signed, error } = await supabase.storage
              .from("profile-pictures")
              .createSignedUrl(filePath, 60 * 60);
            if (!error) {
              setSignedProfilePicUrl(signed?.signedUrl ?? null);
            }
          });
        }}
      />

      <p className="text-sm text-slate-500 mb-6">
        Angemeldet als: <strong className="text-slate-300">{user?.email}</strong>
      </p>

      <VisibilityLegend />

      <ProfileField
        label="Vorname"
        value={profile.first_name}
        onChange={(e) => handleFieldChange("first_name", e.target.value)}
        isSaving={savingField === "first_name"}
        /*
        rightElement={
          <VisibilityIcons
            value={profile.first_name_visibility || "all"}
            onChange={(newVal) =>
              handleFieldChange("first_name_visibility", newVal)
            }
            isSaving={savingField === "first_name_visibility"}
          />
          }
          */
      />

      <ProfileField
        label="Nachname"
        value={profile.last_name}
        onChange={(e) => handleFieldChange("last_name", e.target.value)}
        isSaving={savingField === "last_name"}
        /*
        rightElement={
          <VisibilityIcons
            value={profile.last_name_visibility || "all"}
            onChange={(newVal) =>
              handleFieldChange("last_name_visibility", newVal)
            }
            isSaving={savingField === "last_name_visibility"}
          />
        }
          */
      />

      <ProfileField
        label="Notfallkontakt Name"
        value={profile.emergency_contact_name}
        onChange={(e) =>
          handleFieldChange("emergency_contact_name", e.target.value)
        }
        isSaving={savingField === "emergency_contact_name"}
        rightElement={
          <VisibilityIcons
            value={profile.emergency_contact_visibility || "all"}
            onChange={(newVal) =>
              handleFieldChange("emergency_contact_visibility", newVal)
            }
            isSaving={savingField === "emergency_contact_visibility"}
          />
        }
      />

      <ProfileField
        label="Notfallkontakt Telefon"
        value={profile.emergency_contact_phone}
        onChange={(e) =>
          handleFieldChange("emergency_contact_phone", e.target.value)
        }
        isSaving={savingField === "emergency_contact_phone"}
        rightElement={
          <VisibilityIcons
            value={profile.emergency_contact_visibility || "all"}
            onChange={(newVal) =>
              handleFieldChange("emergency_contact_visibility", newVal)
            }
            isSaving={savingField === "emergency_contact_visibility"}
          />
        }
      />

      <ProfileField
        label="Allergien"
        value={profile.allergies}
        onChange={(e) => handleFieldChange("allergies", e.target.value)}
        isSaving={savingField === "allergies"}
        rightElement={
          <VisibilityIcons
            value={profile.allergies_visibility || "all"}
            onChange={(newVal) =>
              handleFieldChange("allergies_visibility", newVal)
            }
            isSaving={savingField === "allergies_visibility"}
          />
        }
      />

      <ProfileField
        label="Medikamente"
        value={profile.medications}
        onChange={(e) => handleFieldChange("medications", e.target.value)}
        isSaving={savingField === "medications"}
        rightElement={
          <VisibilityIcons
            value={profile.medications_visibility || "all"}
            onChange={(newVal) =>
              handleFieldChange("medications_visibility", newVal)
            }
            isSaving={savingField === "medications_visibility"}
          />
        }
      />

      <ProfileField
        label="Wichtige Hinweise"
        value={profile.notes}
        onChange={(e) => handleFieldChange("notes", e.target.value)}
        isSaving={savingField === "notes"}
        rightElement={
          <VisibilityIcons
            value={profile.notes_visibility || "all"}
            onChange={(newVal) => handleFieldChange("notes_visibility", newVal)}
            isSaving={savingField === "notes_visibility"}
          />
        }
      />

      <ProfileField
        label="Geburtsdatum"
        type="date"
        value={profile.birthdate}
        onChange={(e) => handleFieldChange("birthdate", e.target.value)}
        isSaving={savingField === "birthdate"}
        rightElement={
          <VisibilityIcons
            value={profile.birthdate_visibility || "all"}
            onChange={(newVal) =>
              handleFieldChange("birthdate_visibility", newVal)
            }
            isSaving={savingField === "birthdate_visibility"}
          />
        }
      />

      <SelectField
        label="Essverhalten"
        value={profile.diet}
        onChange={(e) => handleFieldChange("diet", e.target.value)}
        options={dietOptions}
        isSaving={savingField === "diet"}
        rightElement={
          <VisibilityIcons
            value={profile.diet_visibility || "all"}
            onChange={(newVal) => handleFieldChange("diet_visibility", newVal)}
            isSaving={savingField === "diet_visibility"}
          />
        }
      />

      <SelectField
        label="Geschlecht"
        value={profile.gender}
        onChange={(e) => handleFieldChange("gender", e.target.value)}
        options={genderOptions}
        isSaving={savingField === "gender"}
        rightElement={
          <VisibilityIcons
            value={profile.gender_visibility || "all"}
            onChange={(newVal) =>
              handleFieldChange("gender_visibility", newVal)
            }
            isSaving={savingField === "gender_visibility"}
          />
        }
      />

      <SelectField
        label="Kleidergröße"
        value={profile.clothing_size}
        onChange={(e) => handleFieldChange("clothing_size", e.target.value)}
        options={sizeOptions}
        isSaving={savingField === "clothing_size"}
        rightElement={
          <VisibilityIcons
            value={profile.clothing_size_visibility || "all"}
            onChange={(newVal) =>
              handleFieldChange("clothing_size_visibility", newVal)
            }
            isSaving={savingField === "clothing_size_visibility"}
          />
        }
      />

      <MultiSelectInput
        label="Sprachen"
        options={topLanguages}
        selected={profile.languages || []}
        onChange={(newList) => handleFieldChange("languages", newList)}
        isSaving={savingField === "languages"}
        rightElement={
          <VisibilityIcons
            value={profile.languages_visibility || "all"}
            onChange={(newVal) =>
              handleFieldChange("languages_visibility", newVal)
            }
            isSaving={savingField === "languages_visibility"}
          />
        }
      />

      <CheckboxField
        label="Kann schwimmen?"
        value={profile.can_swim}
        onChange={(val) => handleFieldChange("can_swim", val)}
        isSaving={savingField === "can_swim"}
        rightElement={
          <VisibilityIcons
            value={profile.can_swim_visibility || "all"}
            onChange={(newVal) =>
              handleFieldChange("can_swim_visibility", newVal)
            }
            isSaving={savingField === "can_swim_visibility"}
          />
        }
      />

      <CheckboxField
        label="Reiseversicherung vorhanden?"
        value={profile.has_insurance}
        onChange={(val) => handleFieldChange("has_insurance", val)}
        isSaving={savingField === "has_insurance"}
        rightElement={
          <VisibilityIcons
            value={profile.has_insurance_visibility || "all"}
            onChange={(newVal) =>
              handleFieldChange("has_insurance_visibility", newVal)
            }
            isSaving={savingField === "has_insurance_visibility"}
          />
        }
      />

      <MultiSelectInput
        label="Führerscheine"
        selected={profile.licenses || []}
        onChange={(newList) => handleFieldChange("licenses", newList)}
        options={LICENSE_OPTIONS}
        isSaving={savingField === "licenses"}
        rightElement={
          <VisibilityIcons
            value={profile.licenses_visibility || "all"}
            onChange={(newVal) =>
              handleFieldChange("licenses_visibility", newVal)
            }
            isSaving={savingField === "licenses_visibility"}
          />
        }
      />

      <MultiSelectInput
        label="Benachrichtigungspräferenzen"
        selected={profile.notification_preference || []}
        onChange={(newList) =>
          handleFieldChange("notification_preference", newList)
        }
        options={NOTIFICATION_OPTIONS}
        isSaving={savingField === "notification_preference"}
        rightElement={
          <VisibilityIcons
            value={profile.notification_preference_visibility || "all"}
            onChange={(newVal) =>
              handleFieldChange("notification_preference_visibility", newVal)
            }
            isSaving={savingField === "notification_preference_visibility"}
          />
        }
      />
      <PushSettingsSection
        profile={profile}
        onUpdate={handleFieldChange}
        savingField={savingField}
      />

      {user?.id && <PushPermissionSettings userId={user.id} />}

      {user?.id && <EmailSettingsSection userId={user.id} />}

      {/* Passwort ändern */}
      <hr className="my-6 border-[#2a3347]" />
      <p className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-4">Passwort ändern</p>
      <label className="block mb-2 text-sm text-slate-400 font-medium">Aktuelles Passwort</label>
      <input
        type="password"
        className="w-full p-2 mb-4 border border-[#2a3347] rounded bg-[#0d1117] text-slate-100"
        value={oldPassword}
        onChange={(e) => setOldPassword(e.target.value)}
        placeholder="Altes Passwort"
      />
      <label className="block mb-2 text-sm text-slate-400 font-medium">Neues Passwort</label>
      <input
        type="password"
        className="w-full p-2 mb-4 border border-[#2a3347] rounded bg-[#0d1117] text-slate-100"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        placeholder="Neues Passwort"
      />
      <button
        onClick={handlePasswordChange}
        className="w-full bg-sky-500 text-white py-2 rounded hover:bg-sky-600 transition"
      >
        Passwort ändern
      </button>

      <hr className="my-6 border-[#2a3347]" />
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={handleLogout}
          className="w-full bg-[#1e2639] text-slate-300 py-2 rounded hover:bg-[#2a3347] border border-[#2a3347] transition"
        >
          Abmelden
        </button>
      </div>

      {success && <p className="text-emerald-400 mt-4">{success}</p>}
      {error && <p className="text-red-400 mt-4">{error}</p>}

      {/* Links: Feedback & FAQ */}
      <hr className="my-6 border-[#2a3347]" />
      <div className="flex flex-col gap-2 text-center">
        <a
          href="/feedback"
          className="text-sm text-slate-400 hover:text-sky-400 transition-colors"
        >
          💡 Feedback geben
        </a>
        <a
          href="/faq"
          className="text-sm text-slate-400 hover:text-sky-400 transition-colors"
        >
          ❓ FAQ & Hilfe
        </a>
        <a
          href="/rechtliches"
          className="text-xs text-slate-600 hover:text-slate-400 transition-colors"
        >
          Rechtliches & Datenschutz
        </a>
      </div>
    </div>
    </div>
    </div>

    {showPurchaseModal && (
      <CreditPurchaseModal
        currentBalance={creditBalance}
        onClose={() => setShowPurchaseModal(false)}
      />
    )}
    </>
  );
}
